import { useState } from 'react';
import { api } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import { dateTimeLabel } from '../../lib/format';
import { Banner, EmptyState, LoadingPage, Tag } from '../../components/ui';
import { StatusTag, usd, pctLabel } from './shared';
import type { ReportStatus } from '../../lib/financeTypes';

interface Entry {
  id: string;
  periodYear: number | null;
  periodMonth: number | null;
  label: string;
  version: number;
  status: ReportStatus;
  current: boolean;
  uploadedAt: string;
  approvedAt: string | null;
  operatingCash: number | null;
  replacementCash: number | null;
  receivables: number | null;
  payables: number | null;
  monthRevenue: number | null;
  monthExpenses: number | null;
  monthNetOperating: number | null;
  pastRetention: boolean;
}

export function History() {
  const state = useAsync(() => api.finTimeline(), []);
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const compare = useAsync(
    async () => (a && b && a !== b ? api.finCompare(a, b) : null),
    [a, b],
  );
  const data = state.data as { retentionYears: number; entries: Entry[] } | null;

  if (!data) return state.loading ? <LoadingPage label="Loading the timeline…" /> : null;
  if (data.entries.length === 0) {
    return <EmptyState title="No statements on the timeline yet." hint="Uploads appear here as soon as they are stored, including versions that were later superseded." />;
  }

  const comparable = data.entries.filter((e) => e.operatingCash !== null);

  return (
    <div>
      <p className="mut">
        Every upload is kept. Only the approved version of a month is current, so superseded versions never add into totals a second time.
        Statements older than {data.retentionYears} years are flagged against the retention setting.
      </p>

      <div className="card tw">
        <h3>Monthly timeline</h3>
        <table>
          <thead>
            <tr>
              <th>Period</th>
              <th>Status</th>
              <th>Uploaded</th>
              <th className="num">Operating cash</th>
              <th className="num">Replacement cash</th>
              <th className="num">Receivables</th>
              <th className="num">Payables</th>
              <th className="num">Revenue</th>
              <th className="num">Expenses</th>
              <th className="num">Net operating</th>
            </tr>
          </thead>
          <tbody>
            {data.entries.map((e) => (
              <tr key={e.id}>
                <td>
                  {e.label} <span className="mut">v{e.version}</span>
                  {e.current && <Tag tone="ok">Current</Tag>}
                  {e.pastRetention && <Tag tone="warn">Past retention</Tag>}
                </td>
                <td><StatusTag status={e.status} /></td>
                <td className="mut">{dateTimeLabel(e.uploadedAt)}{e.approvedAt ? ` · approved ${dateTimeLabel(e.approvedAt)}` : ''}</td>
                <td className="num">{usd(e.operatingCash)}</td>
                <td className="num">{usd(e.replacementCash)}</td>
                <td className="num">{usd(e.receivables)}</td>
                <td className="num">{usd(e.payables)}</td>
                <td className="num">{usd(e.monthRevenue)}</td>
                <td className="num">{usd(e.monthExpenses)}</td>
                <td className={`num ${(e.monthNetOperating ?? 0) < 0 ? 'bad' : ''}`}>{usd(e.monthNetOperating)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <div className="card-head"><h3>Compare two periods</h3></div>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label className="field" style={{ minWidth: 200 }}>
            <span className="label-text">Earlier</span>
            <select value={a} onChange={(e) => setA(e.target.value)}>
              <option value="">Choose…</option>
              {comparable.map((e) => <option key={e.id} value={e.id}>{e.label} v{e.version} ({e.status})</option>)}
            </select>
          </label>
          <label className="field" style={{ minWidth: 200 }}>
            <span className="label-text">Later</span>
            <select value={b} onChange={(e) => setB(e.target.value)}>
              <option value="">Choose…</option>
              {comparable.map((e) => <option key={e.id} value={e.id}>{e.label} v{e.version} ({e.status})</option>)}
            </select>
          </label>
        </div>
        {compare.data && <CompareView data={compare.data as Record<string, unknown>} />}
        {compare.error && <Banner kind="error">{compare.error}</Banner>}
      </div>
    </div>
  );
}

function CompareView({ data }: { data: Record<string, unknown> }) {
  const left = data.a as { label: string; version: number; status: string };
  const right = data.b as { label: string; version: number; status: string };
  const metrics = data.metrics as Array<{ key: string; label: string; a: number | null; b: number | null; change: number | null; changePct: number | null }>;
  const movers = (data.movers as Array<{ fund: string; kind: string; label: string; a: number; b: number; change: number }>) ?? [];
  return (
    <div style={{ marginTop: 12 }}>
      <p className="mut">{left.label} v{left.version} → {right.label} v{right.version}</p>
      <div className="tw">
        <table>
          <thead><tr><th>Metric</th><th className="num">{left.label}</th><th className="num">{right.label}</th><th className="num">Change</th></tr></thead>
          <tbody>
            {metrics.map((m) => (
              <tr key={m.key}>
                <td>{m.label}</td>
                <td className="num">{usd(m.a)}</td>
                <td className="num">{usd(m.b)}</td>
                <td className={`num ${(m.change ?? 0) < 0 ? 'bad' : 'ok'}`}>
                  {m.change === null ? '—' : `${m.change > 0 ? '+' : ''}${usd(m.change)}`}
                  {m.changePct !== null && <span className="mut"> ({pctLabel(m.changePct, 0)})</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {movers.length > 0 && (
        <>
          <h3>Largest line movers (month actual)</h3>
          <div className="tw">
            <table>
              <thead><tr><th>Line</th><th className="num">{left.label}</th><th className="num">{right.label}</th><th className="num">Change</th></tr></thead>
              <tbody>
                {movers.slice(0, 20).map((m) => (
                  <tr key={`${m.fund}-${m.label}`}>
                    <td>{m.label}<div className="mut">{m.fund} · {m.kind}</div></td>
                    <td className="num">{usd(m.a)}</td>
                    <td className="num">{usd(m.b)}</td>
                    <td className={`num ${m.change < 0 ? 'bad' : 'ok'}`}>{m.change > 0 ? '+' : ''}{usd(m.change)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
